<template>
  <PresentPublication
    v-if="!hasErrors"
    @PresentPublicationEvent="presentPublicationEventHandler"
  />
</template>

<script>
import { mapGetters } from 'vuex';
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('_slug.vue');

import PublicationsTypesEnum from '@shared/enums/PublicationsTypesEnum';
import PresentPublicationEventsEnum from '@/enums/PresentPublicationEventsEnum';
import BookScrollEnum from '@/enums/BookScrollEnum';
import CustomErrorEnum from '@/enums/CustomErrorEnum';
import BrandsEnum from '@shared/enums/BrandsEnum.mjs';
import AppStateEnum from '@/enums/AppStateEnum';
import AssetResourcesEnum from '@/enums/AssetResourcesEnum';
import { sizes as thumbSize } from '@shared/enums/ThumbSizesEnum';

import pageCommonMixin from '@/components/mixins/pageCommonMixin';
import PresentPublication from '@/components/views/PresentPublication/PresentPublication';

import Utils from '@/services/utils/Utils';
import AgentService from '@/services/Agent/AgentService';
import QueryParamService from '@/services/QueryParamService';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import AssetsManager from '@/services/AssetsManager/AssetsManager';
import PublicationDownloadLogicService from '@/services/PublicationLogic/PublicationDownloadLogicService';
import PublicationLogicService from '@/services/PublicationLogic/PublicationLogicService';
import PublicationErrorHandlingLogicService from '@/services/PublicationLogic/PublicationErrorHandlingLogicService';
import PublicationNavigateLogicService from '@/services/PublicationLogic/PublicationNavigateLogicService';
import AppService from '@/services/AppService';

async function initPublication(store, route) {
  _resetStores(store);
  const response = await _initStoreStates(store, route);
  _updateAfterInit(store);
  return response;
}

function _resetStores(store) {
  store.dispatch('OpenParameterStore/destroyOpenParams');
  store.dispatch('PlaybackStore/reinit');
  store.commit('ProgressStore/resetStore');
  store.commit('CompilationsStore/setIsEditMode', false);
  store.commit('PublicationStore/setParaPlayButtonPressed', false);
  store.commit('PublicationStore/setFabButtonPressed', false);
  store.commit('PublicationStore/setIsApplyAllParams', false);
  store.commit('ContextStore/setIsPresentPublicationMounted', false);

  const excludeInitProps =
    store.getters['PublicationStore/getExcludeInitProps'];
  store.dispatch('PublicationStore/destroyPublicationState', excludeInitProps);
}

function _updateAfterInit(store) {
  const hasSearchNavigation =
    store.getters['OpenParameterStore/hasSearchNavigation'];
  if (!hasSearchNavigation) {
    store.dispatch('BookStore/resetSearchNavigation');
  }
}

async function _initStoreStates(store, route) {
  const errors = [];
  store.dispatch('PublicationStore/setPublicationScrollStategy');
  await store.dispatch('LibraryStore/initStateByPublicationTypes', {
    publicationsTypes: [
      PublicationsTypesEnum.BOOK,
      PublicationsTypesEnum.COMPILATION
    ],
    forceRemote: false
  });
  const openParams = await _initOpenParams(store, route);
  if (!openParams) {
    const error = new Error('Open paramters empty');
    error.type = CustomErrorEnum.EMPTY_DATA;
    errors.push(error);
    return { errors };
  }
  const { publicationId } = openParams;
  store.dispatch('ReadingSettingsStore/initDefaultPublicationSettings', {
    publicationId
  });
  if (process.client) {
    await store.dispatch('AssessmentStore/initLatestResult');
  }
  return { errors };
}

async function _initOpenParams(store, route, parsedParams = {}) {
  const openParams = await store.dispatch(
    'OpenParameterStore/buildOpenParameters',
    {
      ...route.params,
      ...parsedParams
    }
  );
  return openParams;
}

function _parseFromUrlOpenParams(router) {
  if (process.server || !router) {
    return {};
  }
  const {
    paraId,
    selectionString,
    openOffset
  } = QueryParamService.getAllParams(router);
  const openQueryParamsKeys = ['paraId', 'selectionString', 'openOffset'];
  QueryParamService.removeParams(router, openQueryParamsKeys);

  const openParams = {};
  if (paraId) {
    openParams.isOpenFromLink = true;
    openParams.paragraphId = paraId;
  }
  if (openOffset) {
    const parsedOpenOffset = parseInt(openOffset, 10);
    openParams.openOffset = isNaN(parsedOpenOffset) ? 0 : parsedOpenOffset;
  }
  if (selectionString) {
    openParams.highlight = {
      selectionString: selectionString
    };
  }
  return openParams;
}

function _parseCourseUrl(router) {
  if (process.server || !router) {
    return '';
  }
  const { courseUrl = '', courseName = '' } = QueryParamService.getAllParams(
    router
  );
  QueryParamService.removeParams(router, ['courseUrl', 'courseName']);
  return {
    courseUrl: decodeURIComponent(courseUrl),
    courseName: decodeURIComponent(courseName.replace(/\+/g, '%20'))
  };
}

async function _isNeedToRestoreReadingPosition(store, openParams) {
  const publicationId = openParams?.publicationId;
  const publicationType = openParams?.publicationType;
  const previousPosition = await store.dispatch(
    'ProgressStore/loadPreviousReadingPosition',
    {
      publicationId,
      publicationType
    }
  );

  const needToRestoreReadingPosition =
    !openParams?.paraId && previousPosition?.openLocator;
  return needToRestoreReadingPosition;
}

function getHeadInfo(store, bookMeta) {
  const bookId = bookMeta?.id || bookMeta?._id;
  if (!bookId) {
    logger.warn('No publicationId found');
    return store.getters['ContextStore/getBookPageEmptyHeadInfo'];
  }
  return store.dispatch('ContextStore/getBookPageHeadInfo', bookMeta);
}

let isBookSeoFetched = false;

export default {
  name: 'SlugPage',
  components: { PresentPublication },
  mixins: [pageCommonMixin],
  async asyncData(context) {
    logger.info('Start load open params');
    const { store, route } = context;

    const isAuthenticated = store.getters['UserStore/isAuthenticated'];
    const isDevice = store.getters['ContextStore/isDevice'];
    if (isDevice && isAuthenticated) {
      AppService.initAppReaderAfterStart(store);
    }
    const { errors } = await initPublication(store, route);
    const publicationId = store.getters['OpenParameterStore/getPublicationId'];
    let bookMeta = null;
    try {
      bookMeta = publicationId
        ? await store.dispatch('PublicationStore/readMeta', publicationId)
        : null;
      if (bookMeta) {
        await store.dispatch('PublicationStore/fetchBookSeo', publicationId);
        isBookSeoFetched = true;
      }
    } catch (error) {
      logger.warn("Can't load book meta");
    }
    const { meta, link, title } = await getHeadInfo(store, bookMeta);
    const bookImageUrl = bookMeta
      ? await AssetsManager.buildBookCoverPath(
          bookMeta.id,
          bookMeta.cover,
          thumbSize.LARGE,
          AssetResourcesEnum.REMOTE
        )
      : null;
    return { meta, link, title, bookMeta, bookImageUrl, errors };
  },
  data() {
    return {
      errors: [],
      hideMetablock: false
    };
  },
  jsonld() {
    if (!this.bookMeta || this.bookMeta.type !== PublicationsTypesEnum.BOOK) {
      return null;
    }

    const langName = this.$store.getters[
      'ContextStore/getSupportedLanguageByCode'
    ](this.bookMeta.language);
    const bookUrl = new URL(
      this.$store.getters['ContextStore/getClientReaderUrl']
    );
    bookUrl.pathname =
      Utils.pathJoin([bookUrl.pathname, this.bookMeta.id]) + '/';

    const ldJson = {
      '@type': 'Book',
      name: this.bookMeta.name,
      description: this.bookMeta.description,
      URL: bookUrl.href,
      genre: this.bookMeta.category,
      numberOfPages: Math.ceil(
        this.bookMeta.wordsCount / AppConstantsUtil.AVERAGE_PAGE_WORDS_COUNT
      ),
      image: this.bookImageUrl,
      author: {
        '@type': 'Person',
        name: this.bookMeta.author
      },
      inLanguage: {
        '@type': 'Language',
        name: langName,
        alternateName: this.bookMeta.language
      },
      isFamilyFriendly: true
    };
    if (this.$store.getters['ContextStore/brand'] === BrandsEnum.FFA) {
      ldJson.educationalLevel = this.bookMeta.difficulty;
    }
    return ldJson;
  },
  head() {
    const commonClasses = this.getCommonPageClasses();
    const headSettings = this.getCommonHeadSettings();
    const bodyAttrs = headSettings.bodyAttrs;

    const classes = [...commonClasses, ...this.bookStyleClasses];
    const settingsTheme = this.readingSettings.themeName;
    if (settingsTheme) {
      classes.push(settingsTheme);
    }
    logger.info('Add head classes', classes);

    return {
      title: this.title,
      meta: this.meta,
      link: this.link,
      htmlAttrs: {
        lang: this.bookLanguage
      },
      bodyAttrs: {
        ...bodyAttrs,
        class: classes
      }
    };
  },

  computed: {
    ...mapGetters('ContextStore', ['appState']),
    bookStyleClasses() {
      const bookStyleClass = this.$store.getters[
        'PublicationStore/getPublicationStyleClassName'
      ](this.publicationId);

      if (!this.isProgressbarCompact) {
        bookStyleClass.push('expanded-progressbar');
      }

      return bookStyleClass;
    },
    hasErrors() {
      return this.errors.length !== 0;
    },
    readingSettings() {
      return this.$store.getters['ReadingSettingsStore/getReadingSettingsView'];
    },
    publicationId() {
      return this.$store.getters['OpenParameterStore/getPublicationId'];
    },
    openParams() {
      const openParams = this.$store.getters[
        'OpenParameterStore/getPublicationOpenParameters'
      ];
      return openParams;
    },
    editMode() {
      return this.$store.getters['OpenParameterStore/getEditMode'];
    },
    bookLanguage() {
      return this.$store.getters['PublicationStore/getBookLanguage'];
    },
    isProgressbarCompact() {
      return this.$store.getters['ContextStore/getIsProgressbarCompact'];
    }
  },
  async created() {
    if (this.errors.length !== 0 && process.server) {
      this.$router.push({ name: AppStateEnum.MANAGE_PUBLICATION });
      return;
    }
    this.$store.dispatch('ManagePopupStore/startLoader');
    this.$store.dispatch('PublicationStore/transformToClientData');

    if (!this.publicationId) {
      logger.info(`Start load local publication`);
      const { errors } = await initPublication(this.$store, this.$route);
      this.errors = errors;
    }
    if (this.publicationId) {
      try {
        await this.$_addRecentItem();
      } catch (error) {
        logger.warn('Failed to add recent item:', error);
      }
    }

    logger.info(
      `Loaded open params remotePublicationId: ${this.publicationId}`
    );
    this.errors.forEach(error => {
      this.presentPublicationEventHandler({
        type: PresentPublicationEventsEnum.ERROR,
        data: error
      });
    });
  },
  async mounted() {
    this.$_addConsoleUtils();

    // fix for the static book page, in case when SEO data was updated, but static was not regenerated.
    // it updates a book SEO only if it was opened from link or it was page reload.
    if (this.bookMeta && !isBookSeoFetched) {
      await this.$store.dispatch(
        'PublicationStore/fetchBookSeo',
        this.publicationId
      );
    }
  },
  async destroyed() {
    AgentService.syncData();

    const appState = this.$store.getters['ContextStore/appState'];
    if (
      appState !== AppStateEnum.PRESENT_PUBLICATION &&
      appState !== AppStateEnum.MANAGE_ABOUT
    ) {
      _resetStores(this.$store);
    }
  },
  beforeDestroy() {
    this.$store.dispatch('ManagePopupStore/finishLoader');
    this.$store.dispatch('ManagePopupStore/closeReturnToCourseToaster');
  },
  methods: {
    $_addConsoleUtils() {
      if (process.client) {
        window.createNoteFromParaOneByOne = async (...args) => {
          const utils = await import(
            '@/services/PublicationLogic/PublicationConsoleUtilsService'
          );
          utils.default.__createNoteFromParaOneByOne(this.$store, ...args);
        };
        window.deleteNoteFromPara = async (...args) => {
          const utils = await import(
            '@/services/PublicationLogic/PublicationConsoleUtilsService'
          );
          utils.default.__deleteAllAnnotationFromPara(this.$store, ...args);
        };
        window.createNoteFromParaAtMoment = async (...args) => {
          const utils = await import(
            '@/services/PublicationLogic/PublicationConsoleUtilsService'
          );
          utils.default.__createNoteFromParaAtMoment(this.$store, ...args);
        };
      }
    },
    async presentPublicationEventHandler(payload) {
      const { type, data } = payload;
      switch (type) {
        case PresentPublicationEventsEnum.REFRESH_BOOK:
          PublicationNavigateLogicService.openPublication(
            this.$store,
            this.$router,
            { reopen: true }
          );
          break;
        case PresentPublicationEventsEnum.STABLE_SCROLL: {
          this.$store.commit(
            'ContextStore/setIsPresentPublicationMounted',
            true
          );
          const parsedParams = _parseFromUrlOpenParams(this.$router);

          const needToRestoreReadingPosition = await _isNeedToRestoreReadingPosition(
            this.$store,
            this.openParams
          );
          if (
            Object.keys(parsedParams).length !== 0 ||
            needToRestoreReadingPosition
          ) {
            await _initOpenParams(this.$store, this.$route, parsedParams);
            this.$_downloadAutoUpdate(data?.isFirstStableAfterMount);
            this.$_openReturnToCourseToasterIfNeed();
            return;
          }
          this.$store.commit('PublicationStore/setIsApplyAllParams', true);
          PublicationLogicService.openPlayPopup(
            this.$store,
            this.$t.bind(this)
          );
          this.$store.commit('CompilationsStore/setIsEditMode', this.editMode);
          this.$_downloadAutoUpdate(data?.isFirstStableAfterMount);
          this.$_openReturnToCourseToasterIfNeed();
          break;
        }
        case PresentPublicationEventsEnum.STABLE: {
          this.$store.dispatch('ManagePopupStore/finishLoader');
          break;
        }
        case BookScrollEnum.UPDATED_BOOK: {
          const bookId = data.bookId;
          await this.updateRelatedStores(bookId);
          PublicationNavigateLogicService.openPublication(
            this.$store,
            this.$router,
            { reopen: true }
          );
          break;
        }
        case PresentPublicationEventsEnum.ERROR:
          PublicationErrorHandlingLogicService.openBookErrorHandling(
            this.$store,
            this.$t.bind(this),
            data
          );
          this.$store.dispatch('ManagePopupStore/finishLoader');
          break;
        default:
          break;
      }
    },

    async updateRelatedStores() {
      const publicationId = this.$store.getters[
        'OpenParameterStore/getPublicationId'
      ];

      await this.$store.dispatch(
        'PublicationStore/updatePublicationRelatedStores',
        publicationId
      );
    },
    $_addRecentItem() {
      const appMode = this.$store.getters['ContextStore/appModeGetter'];

      return this.$store.dispatch('RecentBookStore/addRecentItem', {
        publicationId: this.publicationId,
        appMode
      });
    },
    $_downloadAutoUpdate(isFirstStableAfterMount) {
      if (isFirstStableAfterMount) {
        PublicationDownloadLogicService.downloadAutoupdate(
          this.$store,
          this.$router
        );
      }
    },
    $_openReturnToCourseToasterIfNeed() {
      const { courseUrl, courseName } = _parseCourseUrl(this.$router);
      if (!courseUrl || !courseName) {
        return;
      }
      this.$store.dispatch('ManagePopupStore/openReturnToCourseToaster', {
        url: courseUrl,
        name: courseName
      });
    }
  }
};
</script>

<style src="@/assets/fonts/fonts-book.css"></style>
